import { useEffect, useRef, useState } from 'react'
import { getBrandConfigs, saveBrandConfig, previewMailTexts, type MailTextKind, type MailTextPreview } from '../api'
import { stableJson } from '../layout/TabsContext'
import RichTextEditor from './RichTextEditor'
import { type FrameCtl } from './MailFrameEditor'
import { MAIL_FRAME_PREVIEW_DEBOUNCE_MS, MAIL_PREVIEW_BACKGROUND, MAIL_PLACEHOLDER_DOCUMENT_URL, MAIL_PLACEHOLDER_DOCUMENT_URL_LIST } from '../../shared/constants'
import { fill, COMPONENTS_BRAND_MAIL_TEXTS_TEXTS as T } from '../../shared/texts'
import { DOCUMENT_URL_TIP, unknownPlaceholderWarning } from '../lookups'
import { useToast } from '../layout/ToastContext'

// The brand's own texts of the invoice, single-document and credit-note mail (operator,
// 2026-10-26, „61b": „Mit freundlichen Grüßen Detlef Stöver passt nicht bei Vitalpilze"). The
// brand's tab of the panel „Mailtexte" on System → Vorlagen → E-Mail; „Allgemein" holds the common ones.
//
// ⛔ Like the frame editor: no save button of its own — the change is reported to the page through
// `bind`, so „Übernehmen", „Verwerfen", the ● and the close guard cover it.
//
// The preview comes from the server, by the SAME rule as the sending (services/mailTexts.ts), with
// the fields as they stand in the mask, saved or not — and says for each mail which text applies.

const FIELDS = [
    'invoiceMailSubject', 'invoiceMailBody',
    'singleInvoiceMailSubject', 'singleInvoiceMailBody',
    'creditNoteMailSubject', 'creditNoteMailBody',
] as const
type Texts = Record<(typeof FIELDS)[number], string>
const EMPTY = Object.fromEntries(FIELDS.map((f) => [f, ''])) as Texts

const KINDS: { kind: MailTextKind, label: string, subject: keyof Texts, body: keyof Texts, hint?: string, placeholder: string }[] = [
    { kind: 'invoice', label: T.invoice, subject: 'invoiceMailSubject', body: 'invoiceMailBody', placeholder: T.commonPlaceholder },
    { kind: 'single', label: T.single, subject: 'singleInvoiceMailSubject', body: 'singleInvoiceMailBody', hint: T.singleHint, placeholder: T.singlePlaceholder },
    { kind: 'creditNote', label: T.creditNote, subject: 'creditNoteMailSubject', body: 'creditNoteMailBody', placeholder: T.commonPlaceholder },
]

export default function BrandMailTexts({ brand, readOnly, bind }: { brand: string, readOnly: boolean, bind?: (ctl: FrameCtl | null) => void }) {
    const [stored, setStored] = useState<Texts | null>(null)
    const [texts, setTexts] = useState<Texts>(EMPTY)
    const [own, setOwn] = useState(false)
    const [error, setError] = useState('')
    const [kind, setKind] = useState<MailTextKind>('invoice')
    const [preview, setPreview] = useState<MailTextPreview[]>([])
    const { toast } = useToast()

    const load = async () => {
        try {
            const cfg = (await getBrandConfigs()).find((c) => c.brand === brand)
            const t = Object.fromEntries(FIELDS.map((f) => [f, cfg?.[f] ?? ''])) as Texts
            setStored(t); setTexts(t); setOwn(FIELDS.some((f) => t[f].trim())); setError('')
        } catch (e: any) { setError(e?.message ?? T.loadFailed) }
    }
    useEffect(() => { void load() }, [brand]) // eslint-disable-line react-hooks/exhaustive-deps

    // Switched off = all six empty: the common texts apply again.
    const effective = own ? texts : EMPTY
    const dirty = !readOnly && !!stored && stableJson(effective) !== stableJson(stored)

    const latest = useRef({ save: async () => {}, discard: () => {} })
    latest.current = {
        save: async () => {
            await saveBrandConfig(brand, effective); await load()
            const warning = unknownPlaceholderWarning(...FIELDS.map((f) => effective[f]))
            if (warning) toast(warning, 'warn')
        },
        discard: () => { if (stored) { setTexts(stored); setOwn(FIELDS.some((f) => stored[f].trim())) } },
    }
    useEffect(() => {
        bind?.({ dirty, save: () => latest.current.save(), discard: () => latest.current.discard() })
    }, [dirty]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => () => bind?.(null), []) // eslint-disable-line react-hooks/exhaustive-deps

    const key = stableJson([brand, effective])
    useEffect(() => {
        if (!stored) return
        const t = setTimeout(() => {
            previewMailTexts(brand, effective).then((p) => { setPreview(p); setError('') }).catch((e) => setError(e.message))
        }, MAIL_FRAME_PREVIEW_DEBOUNCE_MS)
        return () => clearTimeout(t)
    }, [key, !!stored]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!stored) return error ? <div className="notice err">{error}</div> : null
    const set = (k: keyof Texts, v: string) => setTexts((t) => ({ ...t, [k]: v }))
    const shown = preview.find((p) => p.kind === kind)

    return (
        <div style={{ marginBottom: 18 }}>
            {error && <div className="notice err">{error}</div>}
            {/* Same markup as „eigener Rahmen" in MailFrameEditor. */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px' }}>
                <input type="checkbox" name="brand-own-mail-texts" checked={own} disabled={readOnly}
                    onChange={(e) => setOwn(e.target.checked)} />
                {T.ownToggle}
            </label>
            <p className="hint" style={{ marginTop: 4 }}>{T.ownHint}</p>
            {!own && FIELDS.some((f) => stored[f].trim()) && <p className="hint">{T.offClears}</p>}
            {own && <p className="hint">{T.placeholders} {DOCUMENT_URL_TIP}</p>}

            {/* Which text each mail gets right now — from the server, by the sending's rule. */}
            <table style={{ marginBottom: 10 }}>
                <tbody>
                    {KINDS.map((k) => {
                        const p = preview.find((x) => x.kind === k.kind)
                        return (
                            <tr key={k.kind}>
                                <td>{k.label}</td>
                                <td className="muted">{T.applies}: {T.appliesSubject} — {p ? T.sources[p.subjectSource] : '…'} · {T.appliesText} — {p ? T.sources[p.bodySource] : '…'}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>

            <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
                {own && KINDS.map((k) => (
                    <div key={k.kind}>
                        <h4>{k.label}</h4>
                        {k.hint && <p className="hint" style={{ marginTop: 0 }}>{k.hint}</p>}
                        <div className="field">
                            <label>{T.subject}</label>
                            <input type="text" name={`brand-${k.subject}`} autoComplete="off" placeholder={k.placeholder}
                                value={texts[k.subject]} onChange={(e) => set(k.subject, e.target.value)} />
                        </div>
                        <div className="field">
                            <label>{T.text}</label>
                            <RichTextEditor value={texts[k.body]} onChange={(html) => set(k.body, html)} />
                        </div>
                    </div>
                ))}
            </fieldset>

            <h4>{T.preview}</h4>
            <div className="pill-tabs" role="tablist">
                {KINDS.map((k) => (
                    <button key={k.kind} type="button" role="tab" className={`pill-tab${kind === k.kind ? ' active' : ''}`}
                        onClick={() => setKind(k.kind)}>{k.label}</button>
                ))}
            </div>
            {shown && (
                <>
                    {shown.kind === 'invoice' && <p className="hint" style={{ margin: '6px 0' }}>{fill(T.invoicePreviewHint, { url: `{${MAIL_PLACEHOLDER_DOCUMENT_URL}}`, list: `{${MAIL_PLACEHOLDER_DOCUMENT_URL_LIST}}` })}</p>}
                    <p style={{ margin: '6px 0' }}><span className="muted">{T.previewSubject}</span> <b>{shown.subject}</b></p>
                    {/* sandbox without flags: no script, no form, no navigation out of the preview */}
                    <iframe title={T.previewTitle} sandbox="" srcDoc={shown.html}
                        style={{ width: '100%', height: 420, border: '1px solid var(--line)', borderRadius: 6, background: MAIL_PREVIEW_BACKGROUND }} />
                </>
            )}
        </div>
    )
}
